import { MARKETS_BY_CODE, marketToCountry } from "@/i18n/markets";
import type { SearchResult, SearchParams } from "@/lib/api";
import { useContext } from "react";
import { SsrRequestContext } from "@/lib/ssr-bootstrap";

export function regionalSearchMeta(pathname: string) {
  if (typeof window !== "undefined" &&
    /^(?:www\.)?driveshare\.com$|^(?:events\.driveshare\.com|bookaclassic\.us)$/i.test(window.location.hostname)) return null;
  const market = /^\/([a-z]{2})(?:\/[a-z]{2})?\/(?:search|cars)\/?$/.exec(pathname)?.[1];
  const country = market && marketToCountry(market);
  return market && country && MARKETS_BY_CODE[market]
    ? { country, label: MARKETS_BY_CODE[market].label, pathname: pathname.replace(/\/$/, "") } : null;
}

export function useRegionalSearchPath(fallback: string): string {
  const request = useContext(SsrRequestContext);
  // Wouter's nested market routers expose "/search". Country ownership
  // must use the original request URL, not that router-relative path.
  return request?.pathname || (typeof window !== "undefined" ? window.location.pathname : fallback);
}
type RegionalSearchSeed = { path: string; countryCode: string; searchResult: SearchResult;
  params?: SearchParams; rawSearch?: string; visitorCountry?: string; frozen?: boolean };
export function usePublicSearchQuery(browserSearch: string): string {
  const request = useContext(SsrRequestContext);
  if (request?.seed && typeof request.seed === "object" && "discovery" in request.seed) {
    const discovery = request.seed.discovery as RegionalSearchSeed;
    if (discovery.path === request.pathname && typeof discovery.rawSearch === "string")
      return discovery.rawSearch;
  }
  return browserSearch;
}
export function useRegionalSearchSeed(): RegionalSearchSeed | undefined {
  const request = useContext(SsrRequestContext);
  if (request?.seed && typeof request.seed === "object" && "discovery" in request.seed) {
    const discovery = request.seed.discovery as RegionalSearchSeed;
    if (discovery.path === request.pathname) return { ...discovery, frozen: true };
  }
  if (request?.seed && typeof request.seed === "object" && "searchResult" in request.seed) {
    return request.seed as { path: string; countryCode: string; searchResult: SearchResult };
  }
  if (typeof document === "undefined" || typeof window === "undefined" || window.location.search) return;
  const source = document.getElementById("landing-prerender-data")?.textContent;
  if (!source) return;
  try {
    const seed = JSON.parse(source);
    if (seed.path === window.location.pathname && seed.searchResult) return seed;
  } catch { /* Ordinary non-SSR pages have no search snapshot. */ }
  return undefined;
}
